import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAbsenceDaysCalculation } from '~/data/absences';
import { Enum } from '~/data/enums';
import { usePublicHolidaysInPicker } from '~/data/publicHolidays';
import { AbsenceFormFieldsProps } from '~/features/AbsenceChanges/EditAbsenceDialog/components/AbsenceFormFields/component';
import {
  AbsenceForm,
  resolveAbsenceFormData,
  submitFormAbsence,
} from '~/features/AbsenceChanges/EditAbsenceDialog/data';
import { useAbsenceValidationSchema } from '~/features/AbsenceChanges/EditAbsenceDialog/useAbsenceValidationSchema';
import { EditAbsenceDialogFormProps } from '~/features/AbsenceChanges/EditAbsenceDialog/useEditAbsenceDialogData';
import { useHasChanged } from '~/hooks/useHasChanged';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { YearlyVacationYearOption } from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/components/AddAbsenceDialogContent/data';
import {
  BalancePool,
  BalancePoolCategory,
} from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/useVacationBalances';

type EnumSelectFieldValue = Enum | null | undefined;
export type AbsenceReasonFieldValue = EnumSelectFieldValue;
export type YearFieldValue = EnumSelectFieldValue;

type UseAbsenceFormOutput = {
  isLoading: boolean;
  failedToLoad: boolean;
  isSubmitting: boolean;
  handleFormSubmit: () => void;
  handleClose: () => void;
  absenceFormFieldProps: Omit<AbsenceFormFieldsProps, 'absenceType'>;
};

type PropsType = {
  employeeId: number;
  onSubmitSuccess?: () => void;
  onClose: () => void;
  formProps: EditAbsenceDialogFormProps;
};

export const useAbsenceForm = (props: PropsType): UseAbsenceFormOutput => {
  const { employeeId, onClose, formProps } = props;
  const {
    employee: { legalEntityId },
    selectedAbsence,
    selectedAbsenceType,
    overlappingAbsence,
    balances: {
      getAvailableBalance,
      availableBalances,
      isBalancePoolReasonMatch,
    },
    refreshAbsencesData,
    reasonOptions,
    isLoadingData,
    failedToLoadData,
    isHoursFieldEnabled,
    permissions,
  } = formProps;

  const hasSelectedAbsenceChanged = useHasChanged(
    JSON.stringify(selectedAbsence),
  );
  const hasSelectedAbsenceTypeChanged = useHasChanged(selectedAbsenceType);

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Load data
  const { getCalculatedAbsenceDays } = useAbsenceDaysCalculation({
    employeeId,
  });

  const { publicHolidays, setSelectedRange, setViewedRange } =
    usePublicHolidaysInPicker({
      legalEntities: !!legalEntityId ? [legalEntityId] : [],
    });

  const calculateAbsenceDays = useCallback(
    (startDate: Date, endDate: Date, absenceReasonCode?: string) => {
      return getCalculatedAbsenceDays({
        startDate,
        endDate,
        absenceReasonCode,
        excludeDates: publicHolidays?.map((ph) => ph.date) || [],
      });
    },
    [publicHolidays, getCalculatedAbsenceDays],
  );

  // Form master states
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [yearOptions, setYearOptions] = useState<YearlyVacationYearOption[]>(
    [],
  );

  const { validationSchema } = useAbsenceValidationSchema({
    formProps,
    yearOptions,
  });

  const resolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  const formMethods = useForm<AbsenceForm>({
    defaultValues: resolveAbsenceFormData(selectedAbsence),
    resolver,
  });
  const {
    setValue,
    getValues,
    handleSubmit,
    formState: { dirtyFields },
    control,
    reset,
    resetField,
  } = formMethods;

  // Form reset
  const resetForm = useCallback(() => {
    if (!!selectedAbsence) {
      reset(resolveAbsenceFormData(selectedAbsence));
    } else {
      reset(resolveAbsenceFormData(undefined));
    }
  }, [reset, selectedAbsence]);

  useEffect(() => {
    if (hasSelectedAbsenceChanged || hasSelectedAbsenceTypeChanged) {
      resetForm();
    }
  }, [hasSelectedAbsenceChanged, hasSelectedAbsenceTypeChanged, resetForm]);

  // Update Year options & selection
  const updateYearOptions = useCallback(
    (reasonCode?: string) => {
      if (availableBalances.balancePoolConfigs.yearlyVacationBalanceEnabled) {
        const yearBalanceOptions = availableBalances.balancePools
          .filter((p) => {
            return (
              p.category === BalancePoolCategory.YearlyVacationBalance &&
              !!p.reasons &&
              isBalancePoolReasonMatch(p, reasonCode) &&
              p.balance > 0
            );
          })
          .map((b): YearlyVacationYearOption => {
            return {
              code: b.name,
              name: b.name,
            };
          });
        const updatedYearValue = yearBalanceOptions.slice(-1)[0] || null;

        setYearOptions(yearBalanceOptions);
        setValue('year', updatedYearValue);
      }
    },
    [availableBalances, setValue, isBalancePoolReasonMatch],
  );

  // Update Days value when Dates change
  const useWatchOnDateField = (
    fieldName: 'startDate' | 'endDate',
  ): Date | null => {
    return useWatch({
      control,
      name: fieldName,
      defaultValue: null,
    });
  };

  const absenceReason: AbsenceReasonFieldValue = useWatch({
    control,
    name: 'absenceReason',
    defaultValue: undefined,
  });
  const isAbsenceReasonChanged = useHasChanged(absenceReason);
  const year: YearFieldValue = useWatch({
    control,
    name: 'year',
    defaultValue: undefined,
  });
  const startDate = useWatchOnDateField('startDate');
  const endDate = useWatchOnDateField('endDate');

  const getAvailableBalanceByFields = useCallback(
    (
      absenceReason: AbsenceReasonFieldValue,
      year: YearFieldValue,
      yearlySum?: boolean,
    ) => {
      return !!absenceReason && year !== null
        ? getAvailableBalance(absenceReason, year, yearlySum)
        : undefined;
    },
    [getAvailableBalance],
  );

  const availableBalance = useMemo(() => {
    return getAvailableBalanceByFields(absenceReason, year);
  }, [getAvailableBalanceByFields, absenceReason, year]);

  const getBalanceForAbsenceReason = useCallback(
    (fieldReason: AbsenceReasonFieldValue) => {
      return getAvailableBalanceByFields(fieldReason, undefined, true);
    },
    [getAvailableBalanceByFields],
  );

  const getBalanceForYear = useCallback(
    (year: YearFieldValue): BalancePool | undefined => {
      return !!absenceReason && !!year
        ? getAvailableBalance(absenceReason, year)
        : undefined;
    },
    [getAvailableBalance, absenceReason],
  );

  useEffect(() => {
    // Auto recalculate days
    if (
      !!startDate &&
      !!endDate &&
      (dirtyFields.startDate || dirtyFields.endDate) &&
      !dirtyFields.days
    ) {
      setSelectedRange({ rangeStart: startDate, rangeEnd: endDate });

      const days = calculateAbsenceDays(
        startDate,
        endDate,
        absenceReason?.code,
      );

      setValue('days', days);
    }
    // Update year options
    if (isAbsenceReasonChanged) {
      updateYearOptions(absenceReason?.code);
    }
  }, [
    startDate,
    endDate,
    setValue,
    calculateAbsenceDays,
    absenceReason,
    setSelectedRange,
    isAbsenceReasonChanged,
    updateYearOptions,
    dirtyFields,
  ]);

  const onPickerMonthChange = (month: Date) => {
    setViewedRange({ pickerMonth: month });
  };

  const handleResetDaysField = useCallback(() => {
    const days =
      !!startDate && !!endDate
        ? calculateAbsenceDays(startDate, endDate, absenceReason?.code)
        : 0;
    resetField('days', { keepDirty: false });
    setValue('days', days);
  }, [
    resetField,
    setValue,
    startDate,
    endDate,
    absenceReason,
    calculateAbsenceDays,
  ]);

  // Form action handlers
  const handleClose = useCallback(() => {
    onClose();
    resetForm();
  }, [onClose, resetForm]);

  const handleFormSubmit = useMemo(
    () =>
      handleSubmit((data) => {
        setIsSubmitting(true);
        // Attachment File is not coming through handleSubmit, must get the value separately
        const attachmentFile = getValues('attachment');

        // Process submission
        submitFormAbsence({
          employeeId,
          absenceType: selectedAbsenceType,
          absenceId: selectedAbsence?.uid,
          data: { ...data, attachment: attachmentFile },
        })
          .then(
            () => {
              enqueueSnackbar(
                !!selectedAbsence
                  ? t('change_saved')
                  : t('successfullyAddedTheAbsence'),
                {
                  variant: 'success',
                },
              );
              return refreshAbsencesData().finally(() => {
                handleClose();
              });
            },
            () => {
              enqueueSnackbar(
                !!selectedAbsence
                  ? t('laFailedToSave')
                  : t('failedToAddAbsence'),
                {
                  variant: 'error',
                },
              );
              refreshAbsencesData();
            },
          )
          .finally(() => {
            setIsSubmitting(false);
          });
      }),
    [
      employeeId,
      handleSubmit,
      selectedAbsenceType,
      t,
      enqueueSnackbar,
      refreshAbsencesData,
      handleClose,
      selectedAbsence,
      getValues,
    ],
  );

  // Data load states
  const isLoading = isLoadingData;
  const failedToLoad = failedToLoadData;

  return {
    isLoading,
    failedToLoad,
    isSubmitting,
    handleFormSubmit,
    handleClose,
    absenceFormFieldProps: {
      employeeId,
      formMethods,
      absencesSettings: {
        startDate,
        onPickerMonthChange,
        publicHolidays,
        overlappingAbsence,
        isHoursFieldEnabled,
        daysFieldEditing: {
          canEditDays: permissions.canEditDays,
          onResetDaysField: handleResetDaysField,
        },
      },
      fieldOptions: {
        absenceReasons: reasonOptions,
        yearOptions,
      },
      absenceBalances: {
        availableBalance,
        getBalanceForAbsenceReason,
        getBalanceForYear,
      },
    },
  };
};
